import type { JellyfinItem } from "../api/jellyfin";
import { getPlaybackSourceLabel } from "../utils/playbackSourcePresentation";

export type MediaSource = NonNullable<JellyfinItem["MediaSources"]>[number];
export type MediaStream = NonNullable<MediaSource["MediaStreams"]>[number];

export type LanguageMenuEntry =
  | { kind: "header"; label: string }
  | { kind: "off" }
  | { kind: "audio" | "subtitle"; stream: MediaStream; index: number };

export type PlaybackStat = { label: string; value: string };
export type DiagnosticsSection = "core" | "detection" | "buffer" | "tracks" | "source";

export function formatPlayerTime(seconds: number) {
  const value = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const remainder = value % 60;
  return hours
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
    : `${minutes}:${String(remainder).padStart(2, "0")}`;
}

export function getStreamLabel(stream: MediaStream, fallback: string) {
  return stream.DisplayTitle?.trim() || stream.Title?.trim() || [
    stream.Language?.toUpperCase(),
    stream.Codec?.toUpperCase(),
  ].filter(Boolean).join(" · ") || fallback;
}

export function getStreamDetail(stream: MediaStream) {
  if (stream.Type === "Audio") {
    return [
      stream.Codec?.toUpperCase(),
      stream.ChannelLayout || (stream.Channels ? `${stream.Channels} channels` : undefined),
      stream.BitRate ? `${Math.round(stream.BitRate / 1000)} kbps` : undefined,
      stream.SampleRate ? `${Math.round(stream.SampleRate / 100) / 10} kHz` : undefined,
    ].filter(Boolean).join(" · ");
  }
  return [
    stream.Codec?.toUpperCase(),
    stream.IsForced ? "Forced" : undefined,
    stream.IsExternal ? "External" : undefined,
  ].filter(Boolean).join(" · ");
}

export function buildLanguageMenuEntries(input: {
  audioStreams: MediaStream[];
  subtitleStreams: MediaStream[];
  showAudio: boolean;
  showSubtitles: boolean;
}): LanguageMenuEntry[] {
  return [
    ...(input.showAudio ? [
      { kind: "header" as const, label: "Audio" },
      ...input.audioStreams.map((stream, index) => ({
        kind: "audio" as const,
        stream,
        index,
      })),
    ] : []),
    ...(input.showSubtitles ? [
      { kind: "header" as const, label: "Subtitles" },
      { kind: "off" as const },
      ...input.subtitleStreams.map((stream, index) => ({
        kind: "subtitle" as const,
        stream,
        index,
      })),
    ] : []),
  ];
}

export function buildPlaybackStats(input: {
  source: MediaSource | undefined;
  sources: MediaSource[];
  videoStream: MediaStream | undefined;
  activeAudioStream: MediaStream | undefined;
  position: number;
  buffered: number;
  duration: number;
  playbackQuality?: "original" | "adaptive";
  hdrCapabilities?: string;
}): PlaybackStat[] {
  const { source, videoStream, activeAudioStream } = input;
  return [
    {
      label: "Quality policy",
      value: input.playbackQuality === "adaptive" ? "Adaptive" : "Original fidelity",
    },
    {
      label: "HDR output",
      value: input.hdrCapabilities || "Unknown",
    },
    {
      label: "Playback",
      value: source?.TranscodingUrl
        ? "Transcoding"
        : source?.SupportsDirectPlay
          ? "Direct Play"
          : "Direct Stream",
    },
    {
      label: "Source",
      value: source
        ? getPlaybackSourceLabel(source, Math.max(0, input.sources.indexOf(source)))
        : "Unavailable",
    },
    {
      label: "Resolution",
      value: videoStream?.Width && videoStream?.Height
        ? `${videoStream.Width} × ${videoStream.Height}`
        : "Unknown",
    },
    {
      label: "Video",
      value: [videoStream?.Codec?.toUpperCase(), videoStream?.VideoRangeType]
        .filter(Boolean).join(" · ") || "Unknown",
    },
    {
      label: "Audio",
      value: [
        activeAudioStream?.Codec?.toUpperCase(),
        activeAudioStream?.Language?.toUpperCase(),
        activeAudioStream?.Channels ? `${activeAudioStream.Channels} ch` : undefined,
      ].filter(Boolean).join(" · ") || "Unknown",
    },
    {
      label: "Bitrate",
      value: source?.Bitrate
        ? `${(source.Bitrate / 1_000_000).toFixed(1)} Mbps`
        : "Unknown",
    },
    {
      label: "Position",
      value: `${formatPlayerTime(input.position)} / ${formatPlayerTime(input.duration)}`,
    },
    {
      label: "Buffer",
      value: `${formatPlayerTime(input.buffered)} / ${formatPlayerTime(input.duration)}`,
    },
  ];
}

export function buildDiagnosticsMetrics(input: {
  section: DiagnosticsSection;
  lifecycleStatus: string;
  generation: number;
  surfaceIdentity: string;
  position: number;
  duration: number;
  buffered: number;
  expectedRuntimeTicks?: number;
  paused: boolean;
  seeking: boolean;
  playbackStats: PlaybackStat[];
  source: MediaSource | undefined;
  sources: MediaSource[];
  deliveryLabel: string;
  capturedAt: string;
}): PlaybackStat[] {
  const core: PlaybackStat[] = [
    { label: "Playback state", value: input.lifecycleStatus },
    { label: "Current time", value: formatPlayerTime(input.position) },
    { label: "Duration", value: formatPlayerTime(input.duration) },
    {
      label: "Expected runtime",
      value: input.expectedRuntimeTicks
        ? formatPlayerTime(input.expectedRuntimeTicks / 10_000_000)
        : "n/a",
    },
    { label: "Short point read invalid", value: "n/a" },
    { label: "Observed short runtime", value: "n/a" },
    { label: "Remaining", value: formatPlayerTime(Math.max(0, input.duration - input.position)) },
    {
      label: "Progress",
      value: input.duration > 0
        ? `${Math.round((input.position / input.duration) * 100)}%`
        : "n/a",
    },
    { label: "Effective playback rate", value: "1" },
    { label: "Raw playback rate", value: "1" },
    { label: "Sample/effective rate", value: "n/a" },
    { label: "Paused", value: input.paused ? "Yes" : "No" },
    { label: "Seeking", value: input.seeking ? "Yes" : "No" },
    { label: "Ended", value: input.lifecycleStatus === "ended" ? "Yes" : "No" },
    { label: "Captured at", value: input.capturedAt },
  ];
  const sourceIndex = input.source
    ? Math.max(0, input.sources.indexOf(input.source))
    : 0;
  const bySection: Record<DiagnosticsSection, PlaybackStat[]> = {
    core,
    detection: [
      {
        label: "Method",
        value: input.source?.TranscodingUrl
          ? "Transcode"
          : input.source?.SupportsDirectPlay
            ? "Direct Play"
            : "Direct Stream",
      },
      { label: "State", value: input.lifecycleStatus },
      { label: "Generation", value: String(input.generation) },
      { label: "Surface", value: input.surfaceIdentity },
    ],
    buffer: [
      { label: "Buffered until", value: formatPlayerTime(input.buffered) },
      { label: "Buffer ahead", value: formatPlayerTime(Math.max(0, input.buffered - input.position)) },
      { label: "Position", value: formatPlayerTime(input.position) },
      { label: "Duration", value: formatPlayerTime(input.duration) },
    ],
    tracks: input.playbackStats.filter((entry) =>
      ["Resolution", "Video", "Audio", "Bitrate"].includes(entry.label)),
    source: [
      {
        label: "Selected source",
        value: input.source
          ? getPlaybackSourceLabel(input.source, sourceIndex)
          : "Unavailable",
      },
      { label: "Available versions", value: String(input.sources.length) },
      { label: "Delivery", value: input.deliveryLabel },
      { label: "Source ID", value: input.source?.Id ?? "n/a" },
    ],
  };
  return bySection[input.section];
}
